import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  MenuLinkPermission,
  MenuLinkInfoCollection,
  FullMarketPermission,
  AccessRightsCriteria,
  PermissionGroupStructure,
  ProductSegregation,
  CustomTableSegregation,
  BulkQuoteDeleteSegregation,
  ActionSegregation
} from './segregation-of-duties.model';
import { MultiListBoxItem } from '../../shared-module/cpq-multi-list-box-with-selection/multi-list-box-item.model';
import { KeyValuePair } from "../../shared-module/common/models/KeyValuePair.model";
import { UrlPrefix } from "../../shared-module/common/constants/url-prefix.constants";

@Injectable()
export class SegregationOfDutiesService {

  constructor(private http: HttpClient) { }

  public getMenuLinks(): Observable<MenuLinkInfoCollection> {
    return this.http.get<MenuLinkInfoCollection>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/menuLinkGroups`);
  }

  public applySegregationSettings(state: boolean): Observable<void> {
    return this.http.post<void>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/applied/${state}`, {});
  }

  public areSegregationSettingsApplied(): Observable<boolean> {
    return this.http.get<boolean>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/applied`);
  }

  public setRightsPreference(isEditingRightPreferred: boolean): Observable<void> {
    return this.http.post<void>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/rightsPreference/${isEditingRightPreferred}`, {});
  }

  public save(permissionId: number, menuLinkInfoIds: Array<MenuLinkPermission>): Observable<void> {
    return this.http.post<void>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/${permissionId}`, menuLinkInfoIds);
  }

  public getPermissions(permissionId: number): Observable<Array<MenuLinkPermission>> {
    return this.http.get<Array<MenuLinkPermission>>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/${permissionId}`);
  }

  public getMenuLinkSubitems(cpqPermissionId: number, menuLinkId: number): Promise<Array<MultiListBoxItem>> {
    return this.http.get<Array<MultiListBoxItem>>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/subitems/${cpqPermissionId}/${menuLinkId}`).toPromise();
  }

  public getAvailableAdminNames(): Observable<Array<KeyValuePair<number, string>>> {
    return this.http.get<Array<KeyValuePair<number, string>>>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/users/admins/names`);
  }

  public getSegregationAdmins(): Observable<Array<number>> {
    return this.http.get<Array<number>>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/adminSegregationUsers`);
  }

  public saveSegregationAdmins(segregationAdmins: Array<number>): Observable<void> {
    return this.http.post<void>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/adminSegregationUsers`, segregationAdmins);
  }
  public isReadOnlySection(urlKey: string): Observable<boolean> {
    return this.http.get<boolean>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/isReadOnlySection/${urlKey}/`);
  }

  public getIdForMenuLinkInfoKey(urlKey: string): Observable<number> {
    return this.http.get<number>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/id/${urlKey}/`);
  }

  public canAccessPage(url: string): Observable<boolean> {
    return this.http.get<boolean>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/canAccessPage?url=${url}`);
  }

  public canAccessPageByKey(urlKey: string): Observable<boolean> {
    return this.http.get<boolean>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/canAccessPage?key=${urlKey}`);
  }

  public getAllPricebooks(): Observable<Array<MultiListBoxItem>> {
    return this.http.get<Array<MultiListBoxItem>>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/pricebooks`);
  }

  public getFullMarketPermission(permissionId: number):Observable<Array<number>> {
    return this.http.get<Array<number>>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/fullMarketPermissions/${permissionId}`);
  }

  public saveFullMarketPermission(marketPricebookSegregationPermission: FullMarketPermission):Observable<void> {
    return this.http.post<void>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/fullMarketPermissions`, marketPricebookSegregationPermission);
  }

  public getConfiguredPermissions(): Observable<Array<number>> {
    return this.http.get<Array<number>>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/configuredPermissions`);
  }

  public getAccessRightsCriteria(userId: number): Observable<Array<AccessRightsCriteria>>{
    return this.http.get<Array<AccessRightsCriteria>>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/accessRightsCriteria/${userId}`);
  }

  public getPermissionGroup(permissionGroupId: number): Observable<PermissionGroupStructure>{
    return this.http.get<PermissionGroupStructure>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/permissionGroups/permissions/${permissionGroupId}`);
  }

  public getConfiguredProductSegregationPermissions(): Observable<Array<number>> {
    return this.http.get<Array<number>>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/configuredProductSegregationPermissions`);
  }

  public getProductSegregation(permissionId: number): Observable<ProductSegregation> {
    return this.http.get<ProductSegregation>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/${permissionId}/productSegregation`);
  }

  public saveProductSegregation(productSegregation: ProductSegregation): Observable<void>  {
    return this.http.put<void>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/${productSegregation.permissionId}/productSegregation`, productSegregation);
  }

  public deleteProductSegregation(permissionId: number): Observable<void> {
    return this.http.delete<void>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/${permissionId}/productSegregation`);
  }

  public getCustomTableSegregation(permissionId: number): Observable<CustomTableSegregation> {
    return this.http.get<CustomTableSegregation>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/${permissionId}/customTableSegregation`);
  }

  public saveCustomTableSegregation(customTableSegregation: CustomTableSegregation): Observable<void>  {
    return this.http.put<void>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/${customTableSegregation.permissionId}/customTableSegregation`, customTableSegregation);
  }
  public deleteCustomTableSegregation(customTableSegregation: CustomTableSegregation): Observable<void>  {
    return this.http.delete<void>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/${customTableSegregation.permissionId}/customTableSegregation/${customTableSegregation.id}`);
  }

  public getBulkQuoteDeleteSegregation(permissionId: number): Observable<BulkQuoteDeleteSegregation> {
    return this.http.get<BulkQuoteDeleteSegregation>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/${permissionId}/bulkQuoteDeleteSegregation`);
  }

  public saveBulkQuoteDeleteSegregation(bulkQuoteDeleteSegregation: BulkQuoteDeleteSegregation): Observable<void>  {
    return this.http.put<void>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/${bulkQuoteDeleteSegregation.permissionId}/bulkQuoteDeleteSegregation`, bulkQuoteDeleteSegregation);
  }

  public deleteBulkQuoteDeleteSegregation(bulkQuoteDeleteSegregation: BulkQuoteDeleteSegregation): Observable<void>  {
    return this.http.delete<void>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/${bulkQuoteDeleteSegregation.permissionId}/bulkQuoteDeleteSegregation`);
  }

  public getActionsSegregation(permissionId: number): Observable<ActionSegregation> {
    return this.http.get<ActionSegregation>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/${permissionId}/actionSegregation`);
  }

  public saveActionsSegregation(actionSegregation: ActionSegregation): Observable<void>  {
    return this.http.put<void>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/${actionSegregation.permissionId}/actionSegregation`, actionSegregation);
  }

  public createActionsSegregation(actionSegregation: ActionSegregation): Observable<void>  {
    return this.http.post<void>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/${actionSegregation.permissionId}/actionSegregation`, actionSegregation);
  }

  public deleteActionsSegregation(actionSegregation: ActionSegregation): Observable<void>  {
    return this.http.delete<void>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/segregationOfDuties/${actionSegregation.permissionId}/actionSegregation`);
  }
}
